import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoginService } from '../login.service';
import { ContractStatusType, IContract, IFamilyMember, IUserContract } from 'src/app/model/contract/IContract';
import { PreInitSettingsService } from './pre-init-setting.service';
import { catchError, filter, switchMap, tap, take, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { OktaGroup, UserClaims, UserProfile } from 'src/app/model/okta/userClaims';
import { PGHOktaService } from '../pghokta.service';
import { StorageService } from '../storage.service';
import { isAfter, add } from 'date-fns';
import { ADPResourceService } from 'digital-core';
import { PGHAppLoadService } from '../pgh-app-load.service';
import OktaAuth from '@okta/okta-auth-js';
import { ICustomerInfo, IPlan, IPortalAccountConsent, IPortalProfile, IUserPortalAccount } from 'src/app/model/portal-profile/IPortalProfile';
import { PreferenceConsent } from 'src/app/model/account/preference-center-request.dto';
import { AppConstants } from 'src/app/core/constants';
import { PlanService } from '../plan.service';
import { BulkClaim, Claim, PlanDetail } from 'src/app/model/plan/plan-detail';
import { AFXService } from '../afx.service';
import { AfxRenewalResponse } from 'src/app/model/addons/afx-renewal.dto';
import { GTMLoginEvents } from '../../../model/google-tag-manager/api-status';
import { GoogleAnalyticsService } from '../google-tag-manager.service';
import { PGHIdentityService } from '../pghidentity.service';
import { PGHIdentityAuthStateService } from '../pghidentity-auth-state.service';

const OKTA_TOKEN_STORAGE = 'okta-token-storage';

export interface ISelectedPlan extends IPlan {
  userPortalAccount: IUserPortalAccount;
  planType: string;
  isPrimary: boolean;
}

export interface ISelectedContract extends IContract {
  isPrimary: boolean;
  isExpired: boolean;
  isCancelled: boolean;
  isSuspended: boolean;
  isPTPO: boolean;
  inactiveMembers?: IFamilyMember[];
  title?: string;
  startDate?: Date;
  isEprismContract: boolean;
  primaryPhone: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoggedInUserSettings implements OnDestroy {
  private destroy$ = new Subject<void>();
  private middleWareUrl: string | null = null;
  private accountPath: string | null = null;
  private tenantId: string | null = null;
  private ptpoId: string | null = null;
  private dealerIds: string | null = null;
  private companyCode: string | null = null;
  private userProfile: UserProfile | null = null;
  private userPortalAccount: IUserPortalAccount | null = null;
  private isGetAllPlanDetailsEnabled: boolean = false;
  private isBulkPlanDetailsEnabled: boolean = false;
  $selectedPlan: BehaviorSubject<ISelectedPlan | null> = new BehaviorSubject<ISelectedPlan | null>(null);
  $selectedPlanDetail: BehaviorSubject<PlanDetail | null> = new BehaviorSubject<PlanDetail | null>(null);
  $planDetails: BehaviorSubject<PlanDetail[] | null> = new BehaviorSubject<PlanDetail[] | null>(null);
  $bulkClaims: BehaviorSubject<BulkClaim[] | null> = new BehaviorSubject<BulkClaim[] | null>(null);
  $plans: BehaviorSubject<IPlan[] | null> = new BehaviorSubject<IPlan[] | null>(null);
  $selectedContract: BehaviorSubject<ISelectedContract | null> = new BehaviorSubject<ISelectedContract | null>(null);
  $contracts: BehaviorSubject<IContract[] | null> = new BehaviorSubject<IContract[] | null>(null);
  $eprismContracts: BehaviorSubject<any[] | null> = new BehaviorSubject<any[] | null>(null);
  $userClaims: BehaviorSubject<UserClaims | null> = new BehaviorSubject<UserClaims | null>(null);
  $hasActiveAccount: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  $customerInfo: BehaviorSubject<ICustomerInfo | null> = new BehaviorSubject<ICustomerInfo | null>(null);
  $limitedMode: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $afxRenewal: BehaviorSubject<AfxRenewalResponse | null> = new BehaviorSubject<AfxRenewalResponse | null>(null);

  /**
   * Most-recent claims for surfaces like the dashboard's "service requests" carousel.
   *
   * `$bulkClaims` only ever populates on bulk/multi-policy clients (`Global_Bulk_PlanDetails_Enabled`)
   * and is the union of claims across every policy, not just the selected one — so it wins when
   * present. Clients where the bulk pipeline never ran (`$bulkClaims` stays null) fall back to the
   * selected plan's own `claims`, matching pre-bulk behavior exactly.
   */
  readonly $recentClaims: Observable<Claim[]>;

  contractInfoloaded = false;

  constructor(
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private oktaAuthService: OktaAuthStateService,
    private httpClient: HttpClient,
    private preInitSettingsService: PreInitSettingsService,
    private appLoadService: PGHAppLoadService,
    private loginService: LoginService,
    private router: Router,
    private pghOktaService: PGHOktaService,
    private pghIdentityService: PGHIdentityService,
    private storageService: StorageService,
    private resourceService: ADPResourceService,
    private planService: PlanService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private identityAuthStateService: PGHIdentityAuthStateService,
    private afxService: AFXService,
  ) {
    try {
      const settings = this.preInitSettingsService.getSettings();
      if (settings && settings.middleWare) {
        this.middleWareUrl = settings.middleWare.middlewareUrl;
        this.accountPath = settings.middleWare.accountPath;
      } else {
        console.error('Settings or settings.middleWare is undefined in LoggedInUserSettings');
      }

      this.ptpoId = settings?.ptpoId;
    } catch (error) {
      console.error('Error initializing settings in LoggedInUserSettings:', error);
    }

    this.$recentClaims = combineLatest([this.$bulkClaims, this.$selectedPlanDetail]).pipe(
      map(([bulkClaims, selectedPlanDetail]) =>
        bulkClaims !== null ? this.planService.mapBulkClaimsToClaims(bulkClaims) : (selectedPlanDetail?.claims ?? [])
      )
    );

    this.appLoadService.isSiteResourcesLoaded.subscribe(value => {
      if (value) {
        this.tenantId = this.resourceService.getPortalSetting('Global_CTC_TenantId').Value;
        this.companyCode = this.resourceService.getPortalSetting('Global_CTC_CompanyCode').Value;
        this.isBulkPlanDetailsEnabled = this.resourceService.getPortalSetting('Global_Bulk_PlanDetails_Enabled')?.Value === 'true';
        // Bulk plan details supersedes getAllPlanDetails when both are enabled — never run both pipelines at once.
        this.isGetAllPlanDetailsEnabled = !this.isBulkPlanDetailsEnabled && this.resourceService.getPortalSetting('Global_Get_All_PlanDetails_Enabled')?.Value === 'true';
      }
    });

    const authState$ = combineLatest([
      this.oktaAuthService.authState$.pipe(map(state => state?.isAuthenticated ?? false)),
      this.identityAuthStateService.authState$.pipe(map(state => state.isAuthenticated))
    ]).pipe(
      map(([okta, identity]) => okta || identity)
    );

    combineLatest([
      this.appLoadService.isSiteResourcesLoaded,
      authState$
    ]).pipe(
      filter(([a, b]) => a !== null && b !== null)).subscribe(([isLoaded, isAuthenticated]) => {
        this.dealerIds = this.resourceService.getPortalSetting("Global_DealerCodes")?.Value;
        if (isAuthenticated && this.dealerIds) {
          if (this.contractInfoloaded === false) {
            this.contractInfoloaded = true;
            this.setInitialPlan();
            this.userProfile = this.storageService.getLocal<UserProfile>(AppConstants.STORAGE_KEYS.UserProfile);
            this.userPortalAccount = this.storageService.getLocal<IUserPortalAccount>(AppConstants.STORAGE_KEYS.UserPortalAccount);
            const cachedCustomerInfo = this.storageService.getLocal<ICustomerInfo>(AppConstants.STORAGE_KEYS.CustomerInfo);
            if (cachedCustomerInfo) { this.$customerInfo.next(cachedCustomerInfo); }
          }
        }
      });

    // When getAllPlanDetails is enabled, fetch all plan details whenever the plans list changes.
    // Gated on isSiteResourcesLoaded — isGetAllPlanDetailsEnabled is only set once resources load
    // (see the isSiteResourcesLoaded subscribe above), so without this gate a $plans emission that
    // races ahead of that flag would read it as false and silently skip the fetch for good.
    combineLatest([this.$plans, this.appLoadService.isSiteResourcesLoaded])
      .pipe(
        takeUntil(this.destroy$),
        filter((pair): pair is [IPlan[], true] => pair[1] === true && this.isGetAllPlanDetailsEnabled && pair[0] !== null && pair[0].length > 0),
        map(([plans]) => plans),
        switchMap((plans) =>
          this.planService.getAllPlanDetails(plans).pipe(
            tap((planDetails) => {
              this.$planDetails.next(planDetails);
              // Match the plan the user actually had selected (restored from
              // loginService.selectedPlanNumber) rather than always picking index 0 — otherwise
              // every cold load / F5 for a multi-plan user reverts the displayed plan to whichever
              // one happens to come first in the API response.
              const selectedPlanNumber = this.$selectedPlan.value?.planNumber;
              this.$selectedPlanDetail.next(planDetails.find((pd) => pd.planNumber === selectedPlanNumber) ?? planDetails[0] ?? null);
              // getAllPlanDetails has no separate total-count field on its response — derive it
              // from the resolved list length so PlanDetailsTotalCount is populated the same way
              // regardless of which of the two (mutually exclusive) pipelines is enabled.
              this.storageService.setLocal<number>(AppConstants.STORAGE_KEYS.PlanDetailsTotalCount, planDetails.length);
            }),
            catchError(() => {
              this.handlePlanDetailsFetchFailure(plans);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();

    // When bulkPlanDetails is enabled, fetch bulk plan details + claims whenever the plans list changes.
    // Gated on isSiteResourcesLoaded — isBulkPlanDetailsEnabled is only set once resources load
    // (see the isSiteResourcesLoaded subscribe above), so without this gate a $plans emission that
    // races ahead of that flag would read it as false and silently skip the fetch for good.
    combineLatest([this.$plans, this.appLoadService.isSiteResourcesLoaded])
      .pipe(
        takeUntil(this.destroy$),
        filter((pair): pair is [IPlan[], true] => pair[1] === true && this.isBulkPlanDetailsEnabled && pair[0] !== null && pair[0].length > 0),
        map(([plans]) => plans),
        switchMap((plans) =>
          this.planService.getBulkPlanDetails(plans).pipe(
            tap(({ planDetails, count, claims }) => {
              this.$planDetails.next(planDetails);
              // Match the plan the user actually had selected (restored from
              // loginService.selectedPlanNumber) rather than always picking index 0 — otherwise
              // every cold load / F5 for a multi-plan user reverts the displayed plan to whichever
              // one happens to come first in the API response.
              const selectedPlanNumber = this.$selectedPlan.value?.planNumber;
              this.$selectedPlanDetail.next(planDetails.find((pd) => pd.planNumber === selectedPlanNumber) ?? planDetails[0] ?? null);
              this.$bulkClaims.next(claims);
              this.storageService.setLocal<number>(AppConstants.STORAGE_KEYS.PlanDetailsTotalCount, count);
            }),
            catchError(() => {
              this.handlePlanDetailsFetchFailure(plans);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();

    // Subscribe to selectedPlan changes and fetch plan details + AFX renewal eligibility in parallel.
    // Skip the single-plan fetch when getAllPlanDetails/bulkPlanDetails has already populated $planDetails.
    // Gated on isSiteResourcesLoaded so this never races buildPlanDetail's CMS label/setting
    // reads (see PlanService.buildPlanDetail) or the afxRenewalNeeded settings lookup below on
    // a cold load where $selectedPlan can populate before CMS resources do.
    combineLatest([this.$selectedPlan, this.appLoadService.isSiteResourcesLoaded])
      .pipe(
        takeUntil(this.destroy$),
        filter(([plan, isLoaded]): boolean => plan !== null && isLoaded === true),
        switchMap(([plan]) => {
          if ((this.isGetAllPlanDetailsEnabled || this.isBulkPlanDetailsEnabled) && this.$planDetails.value === null) {
            return EMPTY;
          }
          const renewalExcluded = this.isRenewalExcludedForPlanType(plan.planType);
          if (renewalExcluded) {
            // Drop the previous plan's offer right away rather than after getPlanDetails resolves.
            this.$afxRenewal.next(null);
          }
          const afxRenewalNeeded =
            !renewalExcluded &&
            (this.resourceService.getPortalSetting('Global_Renew_Plan_Enabled')?.Value === 'true' || this.resourceService.getPortalSetting('Account_AutoRenewal_Enabled')?.Value === 'true');
          return combineLatest({
            planDetail: this.planService.getPlanDetails(plan.planNumber, plan, false, plan.dealerCode),
            afxRenewal: afxRenewalNeeded ? this.afxService.getAfxRenewal(plan.planNumber) : of(null),
          });
        })
      )
      .subscribe({
        next: ({ planDetail, afxRenewal }) => {
          this.$selectedPlanDetail.next(planDetail);
          this.$limitedMode.next(this.computeLimitedModeFromPlanDetail(planDetail));
          this.$afxRenewal.next(afxRenewal);
        },
        error: () => {
          this.$selectedPlanDetail.next(null);
          this.$limitedMode.next(false);
          this.$afxRenewal.next(null);
        },
      });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  /**
   * Shared failure handler for the getAllPlanDetails/getBulkPlanDetails catchError blocks above.
   * A plan-details fetch failure is only ever fatal (force logout + TechSupport error page) when
   * the batch actually contains an expired plan — everything else (transient errors, a duplicate
   * fetch racing $plans re-emitting mid-navigation, etc.) degrades gracefully instead, matching
   * the single-plan-detail error handler just above.
   *
   * Deliberately reads `plan.status` off the `IPlan[]` already in hand from getPortalProfile()
   * rather than making a follow-up call (e.g. getContractsWithMembers()) to confirm expiration —
   * an expired-90+-days policy is exactly the case whose detail records have been purged
   * downstream, so a second detail lookup fails for the same reason the first one did and would
   * silently misclassify this as "can't confirm, don't log out".
   */
  /**
   * Drops every piece of in-memory user state and re-arms the one-shot contract-info load, so the
   * next authenticated emission loads the incoming user from scratch. For a hand-off login (EZ
   * Pass create-session) that may land in a browser still holding a previous user's session —
   * `contractInfoloaded` is otherwise set once per app instance and would keep the old user's
   * plans. Callers clear storage first; this only resets what's held in memory.
   * Also drops the identity-token auth flag: left true while `contractInfoloaded` is re-armed, the
   * next auth emission would reload a profile with no token and bounce to TechSupport_NoContract.
   */
  resetUserState(): void {
    this.identityAuthStateService.setAuthenticated(false);
    this.contractInfoloaded = false;
    this.$selectedPlan.next(null);
    this.$selectedPlanDetail.next(null);
    this.$planDetails.next(null);
    this.$bulkClaims.next(null);
    this.$plans.next(null);
    this.$selectedContract.next(null);
    this.$contracts.next(null);
    this.$eprismContracts.next(null);
    this.$userClaims.next(null);
    this.$hasActiveAccount.next(null);
    this.$customerInfo.next(null);
    this.$limitedMode.next(false);
    this.$afxRenewal.next(null);
  }

  private handlePlanDetailsFetchFailure(plans: IPlan[]): void {
    const hasExpiredPlan = plans?.some((plan) => plan.status === AppConstants.PLAN_STATUS.Expired);
    if (hasExpiredPlan) {
      this.logoutForExpiredPolicy();
    } else {
      this.degradePlanDetailsGracefully();
    }
  }

  /**
   * A REAL, complete logout — mirrors LogoutComponent's teardown — rather than an in-SPA
   * tokenManager.clear() + router.navigate(). Clearing tokens/storage without actually ending
   * the Okta SSO session leaves stale state behind: a live Okta session cookie can silently
   * re-mint a fresh token on the next guarded navigation (AuthGuard.checkSession()'s
   * getWithoutPrompt() call), and identityAuthState$ never resets on its own — both of which
   * surface as "weird" behavior (e.g. clicking back into the app bounces around instead of
   * staying logged out). Routing through oktaAuth.signOut() instead of just closeSession()
   * ends the session server-side too, and its postLogoutRedirectUri sends the browser straight
   * to this specific error page in one hop instead of bouncing through /login.
   */
  /** Client-level kill switch (authored false on Lowes only); missing/other values keep Okta on. */
  private isOktaEnabled(): boolean {
    return this.resourceService.getPortalSetting('Global_Okta_Enabled')?.Value?.toLowerCase()?.trim() !== 'false';
  }

  private logoutForExpiredPolicy(): void {
    const isOktaEnabled = this.isOktaEnabled();
    if (isOktaEnabled) {
      this.oktaAuth.tokenManager.clear();
    }
    this.identityAuthStateService.setAuthenticated(false);
    this.storageService.clearLocal();
    sessionStorage.clear();
    if (!isOktaEnabled) {
      // No signOut() redirect means no page reload, so drop the in-memory user state ourselves.
      this.resetUserState();
      this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_PolicyExpiredOver90Days' } });
      return;
    }
    const postLogoutRedirectUri = `${window.location.origin}/tech-support?headerKey=TechSupport_PolicyExpiredOver90Days`;
    this.oktaAuth.signOut({ postLogoutRedirectUri });
  }

  private degradePlanDetailsGracefully(): void {
    this.$selectedPlanDetail.next(null);
    this.$limitedMode.next(false);
    this.$afxRenewal.next(null);
  }

  private setInitialPlan() {
    this.getUserClaims().then((userClaims: UserClaims) => {
      this.setUserClaims(userClaims);
      let plans = this.storageService.getLocal<IPlan[]| null>(AppConstants.STORAGE_KEYS.Plans);
      if(plans === null || plans.length === 0) {
        //bring the list of plans
        this.getPortalProfile()
          .pipe(
            // Throw error if there are no contracts
            switchMap((portalProfile) => {
              if (!portalProfile?.plans?.length) {
                return throwError(() => new Error('No plans found for the provided user.'));
              }
              return of(portalProfile);
            }),
            // If error, logout and show no plan modal on login page
            catchError(() => {
              this.loginService.failedLoginBycontract = true;
              if (this.isOktaEnabled()) {
                this.oktaAuth.tokenManager.clear();
                this.oktaAuth.closeSession();
              } else {
                // Re-arm the one-shot profile load and drop the auth flag, otherwise a second lookup in this
                // page never re-emits and loads no plans.
                this.resetUserState();
              }
              this.router.navigate(['/tech-support'], { queryParams: { headerKey: 'TechSupport_NoContract'}});
              return EMPTY;
            })
          )
          .subscribe({
            next: (portalProfile) => {
              this.userProfile = portalProfile.userProfile;
              this.userPortalAccount = portalProfile.userPortalAccount;
              this.setUserProfile();
              this.setUserPortalAccount();
              this.initializePlan(portalProfile.plans);
              this.storageService.setLocal(AppConstants.STORAGE_KEYS.Plans, portalProfile.plans, true);
              if (portalProfile.customerInfo) {
                this.storageService.setLocal(AppConstants.STORAGE_KEYS.CustomerInfo, portalProfile.customerInfo, true);
                this.$customerInfo.next(portalProfile.customerInfo);
              }
            }
          });
      } else {
        this.userProfile = this.storageService.getLocal<UserProfile>(AppConstants.STORAGE_KEYS.UserProfile);
        this.userPortalAccount = this.storageService.getLocal<IUserPortalAccount>(AppConstants.STORAGE_KEYS.UserPortalAccount);
        const cachedCustomerInfo = this.storageService.getLocal<ICustomerInfo>(AppConstants.STORAGE_KEYS.CustomerInfo);
        if (cachedCustomerInfo) {
          this.$customerInfo.next(cachedCustomerInfo);
        }
        this.initializePlan(plans);
        // Refresh plans in async mode to localstorage
        this.getPortalProfile()
          .pipe(
            switchMap((portalProfile) => {
              return of(portalProfile);
            })
          )
          .subscribe({
            next: (portalProfile) => {
              this.storageService.setLocal(AppConstants.STORAGE_KEYS.Plans, portalProfile.plans, true);
              if (portalProfile.customerInfo) {
                this.storageService.setLocal(AppConstants.STORAGE_KEYS.CustomerInfo, portalProfile.customerInfo, true);
                this.$customerInfo.next(portalProfile.customerInfo);
              }
            },
            error: () => {
              // background refresh failure is non-fatal — cached plans + customerInfo remain in storage
            },
          });
      }
    });
  }

  private async getUserClaims(): Promise<UserClaims> {
    const oktaUser = await this.pghOktaService.getUser();
    if (oktaUser) {
      return oktaUser;
    }
    return this.pghIdentityService.getUser();
  }

  private initializePlan(plans: IPlan[]): void {
    // Early return if no plans available
    if (!plans?.length) {
      return;
    }

    this.mapAddreses(plans);

    let lastSelectedPlan = this.tryInitializePlan(plans);

    this.$plans.next(plans);
    this.storageService.setLocal<string>(AppConstants.STORAGE_KEYS.PlansCount, plans?.length?.toString() ?? '0');

    if (!this.isGetAllPlanDetailsEnabled && !this.isBulkPlanDetailsEnabled) {
      if (lastSelectedPlan === null && plans?.length > 1 && this.resourceService.getPortalSetting('Global_SelectHome_Enabled')?.Value?.toLowerCase() !== 'false') {
        //console.warn('No matching selected plan number found in LoginService. Redirecting to Select Plan page.');
        const selectPlanUrl = this.resourceService.getPortalSetting('URL_User_Select_Plan').Value;
        this.router.navigate([selectPlanUrl]);
        return;
      }
    }

    if (lastSelectedPlan === null) {
      this.setSelectedPlan(plans[0]);
    } else {
      this.loginService.selectedPlanNumber = lastSelectedPlan.planNumber;
      this.$selectedPlan.next(lastSelectedPlan);
    }
    const isLoginFlow = localStorage.getItem('gtm_login_success');
    if (isLoginFlow) {
      const isMfaLogin = localStorage.getItem('login_mfa_method');
      if (isMfaLogin) {
        this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.LoginMFA);
        localStorage.removeItem('login_mfa_method');
      }
      const pageReferrer = localStorage.getItem('gtm_login_success');
      if (pageReferrer) {
        this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.LoginSuccess, undefined, pageReferrer);
      }
      localStorage.removeItem('gtm_login_success');
    }
  }

  private tryInitializePlan(plans: IPlan[]): ISelectedPlan | null {
    //console.log('Attempting to initialize selected plan with plan number from LoginService:', this.loginService.selectedPlanNumber);
    //console.log('Available plans:', plans.map(p => p.planNumber).join(', '));

    if (!plans?.length || !this.loginService.selectedPlanNumber) {
      return null;
    }

    const plan = plans.find((plan) => plan.planNumber === this.loginService.selectedPlanNumber);
    if (!plan) {
      return null;
    }

    return {
      ...plan,
      userPortalAccount: this.userPortalAccount,
      planType: this.planService.getPlanType(plan.planCode),
      isPrimary: plan.customers[0].customerType === AppConstants.PLAN_CUSTOMER_TYPE.Primary
    };
  }

  private mapAddreses(plans: IPlan[]) {
    plans.forEach((plan) => {
      let primaryCustomer = plan.customers?.find((customer) => customer.customerType === AppConstants.PLAN_CUSTOMER_TYPE.Primary);
      if (!primaryCustomer) {
        primaryCustomer = plan.customers?.[0];
      }

      let address = primaryCustomer?.addresses?.find((address) => address.addressType === AppConstants.PLAN_ADDRESS_TYPE.Primary);
      if (!address) {
        address = primaryCustomer?.addresses?.[0];
      }

      plan.mainAddress = `${address?.address1}, ${address?.city}, ${address?.state} ${address?.postalCode}`;
      plan.addressLine1 = `${address?.address1}`;
      plan.addressLine2 = `${address?.city}, ${address?.state} ${address?.postalCode}`;
    });
  }

  private initializeContract(elitaContracts: IContract[], eprismContracts: any[], userClaims: UserClaims): void {
    // Early return if no contracts available
    if (!elitaContracts?.length && !eprismContracts?.length) {
      return;
    }

    // Try to initialize with Elita contract first
    let selectedContract = this.tryInitializeElitaContract(elitaContracts, userClaims);

    // Fall back to ePrism contract if no Elita contract available or if Elita contract is expired
    if (!selectedContract || this.isContractRecentlyCancelled(selectedContract)) {
      if (!eprismContracts?.length) {
        this.loginService.failedLoginByContractCancalled = true;
        this.oktaAuth.tokenManager.clear();
        sessionStorage.clear();
        this.router.navigate(['/tech-support'], {
          queryParams: { headerKey: 'Login_FailedLoginModal_Body_Text_For_ContractCancelled' },
        });
        return;
      }

      const eContract = eprismContracts.find(x => x.policyIdentifier.mobileNumber == this.userProfile.primaryPhone);
      selectedContract = this.initializeEprismContract(eContract);
      elitaContracts = [];
    } else {
      this.loginService.failedLoginByContractCancalled = false;
    }

    this.setContracts(elitaContracts, eprismContracts);
    this.setSelectedContract(selectedContract);
    this.updateActiveAccountStatus(elitaContracts, eprismContracts);
  }

  private tryInitializeElitaContract(elitaContracts: IContract[], userClaims: UserClaims): ISelectedContract | null {
    if (!elitaContracts?.length) {
      return null;
    }

    const contract = elitaContracts.find((c) => c.id === this.loginService.selectedContractId) || elitaContracts[0];
    const contractIndex = elitaContracts.findIndex((c) => c.id === this.loginService.selectedContractId) + 1 || 1;
    const contractTitle = elitaContracts.length > 1 ? `Pocket Geek Home Plan ${contractIndex}` : 'Pocket Geek Home Plan';

    return {
      ...contract,
      isPrimary: userClaims.email.toLowerCase() === contract.primaryUserEmail.toLowerCase(),
      isExpired: contract.status === ContractStatusType.Expired,
      isCancelled: contract.status === ContractStatusType.Cancelled,
      isSuspended: contract.subscriberStatusCode === 'S',
      isPTPO: this.isPTPO(contract.dealerId),
      title: contractTitle,
      useGSSPClaims: this.useGSSPClaims(contract.dealerId),
      isEprismContract: false,
      primaryPhone: this.userProfile.primaryPhone,
    };
  }

  private getGracePeriodDays(): number {
    const setting = this.resourceService.getPortalSetting('Global_GracePeriod_Days');
    const parsed = parseInt(setting?.Value ?? '', 10);
    return isNaN(parsed) ? 30 : parsed;
  }

  /**
   * Limited mode disables / hides selected dashboard CTAs (Special Services, etc.)
   * for users whose plan has lapsed and whose grace window for resuming service
   * has elapsed.
   *
   * Returns true only when ALL of the following hold:
   *   1. We have the plan fields needed to compute coverage end (status,
   *      planSalesDate, planTerm). Missing any → bail out as not limited.
   *   2. The plan is not Active. Both Cancelled (explicit) and Expired
   *      (let-it-lapse) qualify — anything that isn't Active is a candidate
   *      for limited mode.
   *      Note: Cancelled will get its own distinct rules in a follow-up;
   *      today the two non-Active statuses are treated identically.
   *   3. The Global_GracePeriod_Limited_Mode_Enabled portal setting is on
   *      (per-client kill switch — Lowes ships off, AHW ships on).
   *   4. Coverage has been over for longer than the configured grace period.
   *      Coverage end = planSalesDate + planTerm months. PlanDetail has no
   *      cancellationDate field, so the boundary is derived from the contract
   *      term rather than the actual cancellation date.
   */
  private computeLimitedModeFromPlanDetail(planDetail: PlanDetail | null): boolean {
    if (!planDetail || !planDetail.status || !planDetail.planSalesDate || !planDetail.planTerm) {
      return false;
    }
    if (planDetail.status.toLowerCase() === AppConstants.PLAN_STATUS.Active.toLowerCase()) {
      return false;
    }
    if (!this.resourceService.isPortalSettingTrue('Global_GracePeriod_Limited_Mode_Enabled')) {
      return false;
    }
    const coverageEnd = add(new Date(planDetail.planSalesDate), { months: planDetail.planTerm });
    const gracePeriodDays = this.getGracePeriodDays();
    // Equivalent to `now > coverageEnd + gracePeriodDays`, rewritten so both
    // sides of the comparison are anchored to a fixed `now` reference.
    return isAfter(add(new Date(), { days: -gracePeriodDays }), coverageEnd);
  }

  private isContractRecentlyCancelled(contract: ISelectedContract): boolean {
    return contract.status === ContractStatusType.Cancelled && contract.cancellationDate && isAfter(add(new Date(), { days: -30 }), new Date(contract.cancellationDate));
  }

  private updateActiveAccountStatus(elitaContracts: IContract[], eprismContracts: any[]): void {
    const cancelledOrExpiredHasFullAccess = this.resourceService.isPortalSettingTrue('Global_CancelledOrExpiredContract_HasFullAccess');

    if (cancelledOrExpiredHasFullAccess) {
      this.$hasActiveAccount.next(true);
      return;
    }

    const hasActiveElitaContract = elitaContracts?.length ? !!elitaContracts.find((c) => c.status === ContractStatusType.Active) : false;
    const hasActiveEprismContract = eprismContracts?.length ? !!eprismContracts.find((c) => c.lineInfo.lineStatus === ContractStatusType.Active) : false;

    this.$hasActiveAccount.next(hasActiveElitaContract || hasActiveEprismContract);
  }

  public initializeEprismContract(contract: any): ISelectedContract {
    const today = new Date();
    const selectedContract: ISelectedContract = {
      primaryPhone: this.userProfile.primaryPhone,
      isPrimary: contract.policyHolderInfo.pahPhone == contract.policyIdentifier.mobileNumber,
      isExpired: Date.parse(contract.coverageInfo.endDate) < Date.parse(today.toLocaleDateString()),
      isCancelled: Date.parse(contract.coverageInfo.endDate) < Date.parse(today.toLocaleDateString()), //TODO:Eprism - This needs to looked at
      isSuspended: contract.lineInfo.lineStatus?.toLowerCase() == "s",
      isPTPO: !!contract.additionalDetails.dealerID ? this.isPTPO(contract.additionalDetails.dealerID) : false, //TODO:Eprism - This needs to looked at
      title: `Protection 360 account`, //TODO:Eprism - This needs to looked at
      useGSSPClaims: !!contract.additionalDetails.dealerID ? this.useGSSPClaims(contract.additionalDetails.dealerID) : false, //TODO:Eprism - This needs to looked at
      dealerId: contract.additionalDetails.dealerID,
      certificateNumberForPhonesPolicy: null, //TODO:Eprism - This needs to looked at
      certificateNumberForGadgetsPolicy: null, //TODO:Eprism - This needs to looked at
      firstName: contract.policyHolderInfo.firstName,
      lastName: contract.policyHolderInfo.lastName,
      addressLine1: contract.policyHolderInfo.streetAddress1,
      city: contract.policyHolderInfo.city,
      state: contract.policyHolderInfo.state,
      zipCode: contract.policyHolderInfo.zipCode,
      id: contract.policyIdentifier.policyNumber,
      primaryUserEmail: contract.policyHolderInfo.email,
      certificateNumberForClientSoldGadgetsPolicy: null,
      startDate: contract.coverageInfo.startDate,
      expirationDate: contract.coverageInfo.endDate,
      grossPolicyPrice: 0, //TODO:Eprism - This needs to looked at
      masterPlanCode: contract.productInfo.insuranceCode, //TODO:Eprism - This needs to looked at
      status: contract.lineInfo.lineStatus,
      subscriberStatusCode: null, //TODO:Eprism - This needs to looked at
      cancellationDate: contract.coverageInfo.endDate, // in Eprism, this will be the cancellation date, will be 9999-12-31 when the contract is active
      inactiveMembers: [],
      isMobilePlanActive: true,
      sku: null,
      members: [{
        firstName: contract.policyHolderInfo.firstName,
        lastName: contract.policyHolderInfo.lastName,
        avatarUrl: contract.policyHolderInfo.avatarUrl,
        abletofileClaim: null,
        contactConsent: null,
        contractNumber: contract.policyIdentifier.policyNumber,
        customerNumber: contract.policyIdentifier.subscriberNumber, //TODO:Eprism - This needs to looked at
        emailAddress: contract.policyHolderInfo.email,
        id: contract.policyHolderInfo.ssn, //TODO:Eprism - This needs to looked at
        language: contract.policyHolderInfo.languagePreference == "E" ? "en" : contract.policyHolderInfo.languagePreference,
        phoneNumber: contract.policyIdentifier.mobileNumber,
        profileColor: contract.policyHolderInfo.profileColor,
        profileSeq: 1, //TODO:Eprism - This needs to looked at
        profileStatus: contract.lineInfo.lineStatus, //TODO:Eprism - This needs to looked at
        relationship: null,
        transactionCode: 0
      }],
      isEprismContract: true
    };
    return selectedContract;
  }

  public clearContractFromCache() {
    this.storageService.removeLocal('ElitaContracts', true);
    this.storageService.removeLocal('EprismContracts', true);
  }

  private setContracts(elitaContracts: IContract[], eprismContracts: any[]) {
    if (elitaContracts?.length) {
      this.$contracts.next(elitaContracts);
    }
    if (eprismContracts?.length) {
      this.$eprismContracts.next(eprismContracts);
    }
    this.storageService.setLocal<string>(`ElitaContractsCount`, elitaContracts?.length?.toString() ?? '0');
    this.storageService.setLocal<string>(`EprismContractsCount`, eprismContracts?.length?.toString() ?? '0');
    this.storageService.setLocal<string>(`IsEprismOnly`, !elitaContracts?.length && !!eprismContracts?.length ? 'true' : 'false');
  }

  private setUserClaims(userClaims: UserClaims) {
    this.$userClaims.next(userClaims);
  }
  private setUserProfile() {
    this.storageService.setLocal(AppConstants.STORAGE_KEYS.UserProfile, this.userProfile, true);
  }

  private setUserPortalAccount() {
    this.storageService.setLocal(AppConstants.STORAGE_KEYS.UserPortalAccount, this.userPortalAccount, true);
  }

  getCurrentUserGroup() {
    const group = new OktaGroup();
    if (localStorage.getItem(OKTA_TOKEN_STORAGE)) {
      const token: any = JSON.parse(localStorage.getItem(OKTA_TOKEN_STORAGE));
      const decodedToken = this.oktaAuth.token.decode(token.accessToken.accessToken);
      //console.log(decodedToken);
      if (decodedToken && decodedToken.payload && decodedToken.payload.groups && Array.isArray(decodedToken.payload.groups) && decodedToken.payload.groups.length) {
        const isGroupExist = decodedToken.payload.groups.some(x => x.toString().toLowerCase().trim() === this.resourceService.getPortalSetting('Global_SiteSettings_GroupName').Value.toLowerCase().trim());
        if (isGroupExist) {
          group.id = this.resourceService.getPortalSetting('Global_SiteSettings_GroupId').Value;
          group.name = this.resourceService.getPortalSetting('Global_SiteSettings_GroupName').Value;
        }
      }
    }
    //console.log(group);
    return group;
  }

  getCurrentGroup() {
    const group = new OktaGroup();
    group.id = this.resourceService.getPortalSetting('Global_SiteSettings_GroupId').Value;
    group.name = this.resourceService.getPortalSetting('Global_SiteSettings_GroupName').Value;
    return group;
  }

  isUserInCurrentGroup() {
    let isExists = false;
    const token: any = JSON.parse(localStorage.getItem(OKTA_TOKEN_STORAGE));
    if (token && token.accessToken && token.accessToken.accessToken) {
      const decodedToken = this.oktaAuth.token.decode(token.accessToken.accessToken);
      if (decodedToken && decodedToken.payload && decodedToken.payload.groups && Array.isArray(decodedToken.payload.groups) && decodedToken.payload.groups.length) {
        isExists = decodedToken.payload.groups.some(x => x.toString().toLowerCase().trim() === this.resourceService.getPortalSetting('Global_SiteSettings_GroupName').Value.toLowerCase().trim());
      }
    }
    return isExists;
  }
  setSelectedPlan(plan: IPlan, isLogin = false) {
    const selectedPlan: ISelectedPlan = {
      ...plan,
      userPortalAccount: this.userPortalAccount,
      planType: this.planService.getPlanType(plan.planCode),
      isPrimary: plan.customers[0].customerType === AppConstants.PLAN_CUSTOMER_TYPE.Primary,
    };
    this.loginService.selectedPlanNumber = selectedPlan.planNumber;
    this.$selectedPlan.next(selectedPlan);
    if (isLogin) {
      const isLoginFlow = localStorage.getItem('gtm_login_success');
      if (isLoginFlow) {
        const isMfaLogin = localStorage.getItem('login_mfa_method');
        if (isMfaLogin) {
          this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.LoginMFA);
          localStorage.removeItem('login_mfa_method');
        }
        const pageReferrer = localStorage.getItem('gtm_login_success');
        if (pageReferrer) {
          this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.LoginSuccess, undefined, pageReferrer);
        }
        localStorage.removeItem('gtm_login_success');
      }
    }
  }

  /**
   * Merges newly-saved Preference Center consent rows into the cached session
   * state (both the in-memory `$selectedPlan` BehaviorSubject and its
   * StorageService-backed copy), without a full `getPortalProfile()` round-trip.
   *
   * Only called after a successful `putPreferenceCenter()` — callers must not
   * invoke this on failure, since it would make the optimistic UI toggle look
   * confirmed when the backend never accepted it.
   */
  updateAccountConsents(consents: PreferenceConsent[]): void {
    const currentPlan = this.$selectedPlan.value;
    if (!currentPlan?.userPortalAccount) {
      return;
    }

    const mergedConsents: IPortalAccountConsent[] = [...(currentPlan.userPortalAccount.consents ?? [])];
    consents
      .filter((consent): consent is PreferenceConsent & { channel: 'Email' | 'Sms' } => consent.channel != null)
      .forEach(consent => {
        const existingIndex = mergedConsents.findIndex(
          existing => existing.purpose === consent.purpose && existing.channel === consent.channel
        );
        if (existingIndex >= 0) {
          mergedConsents[existingIndex] = { ...mergedConsents[existingIndex], optIn: consent.optIn };
        } else {
          mergedConsents.push({ purpose: consent.purpose, channel: consent.channel, optIn: consent.optIn });
        }
      });

    this.userPortalAccount = { ...currentPlan.userPortalAccount, consents: mergedConsents };
    this.setUserPortalAccount();
    this.$selectedPlan.next({ ...currentPlan, userPortalAccount: this.userPortalAccount });
  }

  /**
   * Updates the cached `$afxRenewal` state's `autoRenewConsent` flag in place —
   * both for the optimistic pre-save toggle and for rollback on a failed save —
   * so every consumer of `$afxRenewal` (dashboard renewal notice, Preferences
   * opt-out toggle) stays in sync without a redundant `getAfxRenewal` round-trip.
   * `$afxRenewal` is session-scoped (not StorageService-backed), matching how it's
   * already populated by the `$selectedPlan` pipeline above.
   */
  updateAutoRenewConsent(autoRenewConsent: boolean): void {
    const current = this.$afxRenewal.value;
    if (!current) {
      return;
    }
    this.$afxRenewal.next({ ...current, autoRenewConsent });
  }

  setSelectedContract(selectedContract: ISelectedContract) {
    // filter out inactive members
    selectedContract.inactiveMembers = selectedContract.members.filter(m => m.profileStatus === 'I');
    selectedContract.members = selectedContract.members.filter(m => m.profileStatus !== 'I');
    this.loginService.selectedContractId = selectedContract.id;
    this.$selectedContract.next(selectedContract);
  }

  isPTPO(delaerId: string): boolean {
    return delaerId?.toLowerCase() === this.ptpoId?.toLowerCase();
  }

  getContractsWithMembers(): Observable<IUserContract> {
    const method = 'contractswithmembers';
    const oktaPrefix = this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value.toUpperCase();
    const isElita = this.resourceService.getPortalSetting('Global_CTC_IsElita').Value;
    return this.httpClient.get<IUserContract>(
      `${this.middleWareUrl}${this.accountPath}${method}?isElitaActive=${isElita}&tenantId=${this.tenantId}&dealerIds=${encodeURIComponent(this.dealerIds)}&companyCode=${encodeURIComponent(this.companyCode)}&oktaPrefix=${oktaPrefix}`
    ).pipe(tap(userContract => {
      userContract?.elitaContracts?.forEach(contract => {
        contract.isMobilePlanActive = this.resourceService.isPortalSettingTrue('Global_CTC_IsMobileplanActive');
      });
    }));
  }

  getPortalProfile(): Observable<IPortalProfile> {
    const method = AppConstants.API_ENDPOINTS.PortalProfile;
    return this.httpClient.get<IPortalProfile>(
      `${this.middleWareUrl}${this.accountPath}${method}`
    );
  }

  /**
   * Re-fetches the full plans/customer array from `getPortalProfile()` and patches it back
   * into `$plans` (+ the `Plans` localStorage cache) and `$customerInfo`. `setInitialPlan()`'s
   * own background refresh (see above) only rewrites the localStorage cache for the *next*
   * app load — it never touches the live `$plans` BehaviorSubject — so a save that changes a
   * plan's customer-facing data mid-session (e.g. the cancel-plan flow's confirm-contact-info
   * step) would otherwise leave every live reader of `$plans` (confirm-info's own prefill,
   * cancel-result's navigateToClaims, etc.) showing the pre-save snapshot until a fresh login.
   */
  refreshPlans(): Observable<IPlan[] | null> {
    return this.getPortalProfile().pipe(
      tap((portalProfile) => {
        const plans = portalProfile.plans ?? [];
        this.mapAddreses(plans);
        this.$plans.next(plans);
        this.storageService.setLocal(AppConstants.STORAGE_KEYS.Plans, plans, true);
        if (portalProfile.customerInfo) {
          this.storageService.setLocal(AppConstants.STORAGE_KEYS.CustomerInfo, portalProfile.customerInfo, true);
          this.$customerInfo.next(portalProfile.customerInfo);
        }
      }),
      map((portalProfile) => portalProfile.plans ?? null),
      catchError(() => of(null))
    );
  }

  isFeatureEnabled(dealerCode: string, featureKey: string): boolean {
    return this.isSettingEnabledforDealer(featureKey, dealerCode);
  }

  useGSSPClaims(dealerCode: string): boolean {
    return this.isSettingEnabledforDealer("Global_UseGSSP_Claims_ForDealer", dealerCode);
  }

  isSettingEnabledforDealer(setting: string, dealerCode: string) {
    let portalSetting = this.resourceService.getPortalSetting(setting)?.Value;
    if (portalSetting) {
      if (portalSetting.toLowerCase().trim() == 'all')
        return true;
      else if (portalSetting.toLowerCase().trim() == 'none')
        return false;
      else {
        let isEnabledFor = portalSetting.split(",");
        return isEnabledFor.some(x => x.toLowerCase().trim() === dealerCode.toLowerCase().trim());
      }
    } else return false;
  }

  refreshContractsMembers() {
    // Refresh Contracts in async mode to localstorage
    this.getContractsWithMembers()
      .pipe(
        switchMap(userContract => {
          return of(userContract);
        }),
      )
      .subscribe({
        next: (userContract) => {
          this.initializeContract(userContract.elitaContracts, userContract.eprismContracts, this.$userClaims.value);
          this.storageService.setLocal('ElitaContracts', userContract.elitaContracts, true);
          this.storageService.setLocal('EprismContracts', userContract.eprismContracts, true);
        }
      });
  }

  public get dealerId() {
    return this.dealerIds;
  }

  /**
   * True when plan renewal is switched off for this plan's tier via the CMS setting
   * `Global_Renewal_Excluded_Plan_Types` — a comma-separated list of tiers (Good, Better, Best,
   * Listing), e.g. "Listing". One flag covers every renewal surface: while it matches,
   * `$afxRenewal` stays null, which already hides the renewal notice, closes the /renew route,
   * suppresses the GSSP/coverage-ended modals and the payment-card notice; the Preferences
   * auto-renewal section reads this directly.
   *
   * Compared against `ISelectedPlan.planType`, case-insensitively. Note planType is derived from
   * the planCode prefix (`Global_Plan_Prefix_*`) when the plan is selected, and getPlanType falls
   * back to 'Good' for a planCode matching no prefix (e.g. Lowes) — so listing 'Good' would
   * exclude those too; only list tiers whose prefixes are authored.
   *
   * Fails OPEN: an unset or blank setting, or a missing planType, excludes nothing. The `true`
   * second arg to getPortalSetting suppresses the not-found log — the key is optional.
   */
  isRenewalExcludedForPlanType(planType: string | null | undefined): boolean {
    if (!planType) {
      return false;
    }
    const excludedPlanTypes = (this.resourceService.getPortalSetting('Global_Renewal_Excluded_Plan_Types', true)?.Value ?? '')
      .split(',')
      .map((excluded) => excluded.trim().toLowerCase())
      .filter((excluded) => excluded.length > 0);
    return excludedPlanTypes.includes(planType.toLowerCase());
  }

  /**
   * Re-fetches auto-renewal state directly from AFX and pushes it into `$afxRenewal`.
   * A 2xx from the `afx-renewal-consent` POST does not guarantee AFX persisted exactly
   * what was requested — callers must treat this GET as ground truth rather than
   * trusting the POST's own success signal or the optimistic in-memory patch applied
   * by `updateAutoRenewConsent` before the POST was sent.
   */
  refreshAfxRenewal(planNumber: string): Observable<AfxRenewalResponse> {
    // Renewal is off for this plan type: never fetch, so a refreshRenewal deep link can't
    // re-populate $afxRenewal (and with it the notice / /renew entry) after the gate in the
    // $selectedPlan pipeline kept it null. Completes without emitting.
    if (this.isRenewalExcludedForPlanType(this.$selectedPlan.value?.planType)) {
      return EMPTY;
    }
    return this.afxService.getAfxRenewalStrict(planNumber).pipe(
      tap(afxRenewal => {
        this.$afxRenewal.next(afxRenewal);
      })
    );
  }

  /**
   * Refetches PlanDetail for ALL of the user's plans (not just the selected one) and pushes
   * the result into `$planDetails`. Needed by callers like FindYourCoverageComponent, which
   * reads `$planDetails` for `mostRecentSingleCertPolicyPlan` — that plan may not be the
   * user's *selected* plan, so `refreshSelectedPlanDetails()` (which only refreshes
   * `$selectedPlanDetail` for the selected plan) would leave the LPP card showing stale data.
   *
   * Uses `planService.getAllPlanDetails(plans, true)` rather than hand-rolled cache eviction —
   * `refresh: true` rewrites BOTH the `PlanDetail:all` session cache entry and every per-plan
   * `PlanDetail:<planNumber>` entry (see plan.service.ts), so the `:all` cache and the
   * per-plan caches can't disagree afterwards.
   *
   * Note this always goes through `getAllPlanDetails`, even on a client whose normal load path
   * is `getBulkPlanDetails` — see `refreshPlanDetailByNumber` below for the plan-scoped
   * alternative that stays agnostic of which bulk pipeline populated `$planDetails`.
   */
  refreshAllPlanDetails(): Observable<PlanDetail[] | null> {
    return this.$plans.pipe(
      take(1),
      switchMap((plans) => {
        if (!plans?.length) {
          return of(null);
        }
        return this.planService.getAllPlanDetails(plans, true).pipe(
          tap((planDetails) => {
            this.$planDetails.next(planDetails);
            // Preserve the current selection rather than unconditionally picking planDetails[0]
            // (that fallback belongs to the constructor's initial-load path only) — blindly
            // reassigning here would silently switch the user's selected-plan context after a
            // post-save refresh.
            const selectedPlanNumber = this.$selectedPlan.value?.planNumber;
            const matchingPlanDetail = planDetails.find((pd) => pd.planNumber === selectedPlanNumber) ?? planDetails[0] ?? null;
            this.$selectedPlanDetail.next(matchingPlanDetail);
          }),
          // A failed post-save refresh must not log the user out — do NOT reuse the
          // constructor's $plans error path (clears the Okta session, redirects to /tech-support).
          catchError(() => of(null))
        );
      })
    );
  }

  /**
   * Re-fetches ONE policy's details and patches the result back into `$planDetails` (and into
   * `$selectedPlanDetail` when it happens to be the selected policy).
   *
   * `refreshSelectedPlanDetails()` can only ever refresh `$selectedPlan`. The service-request
   * flow aggregates across every active policy, so after registering or completing an item we
   * have to refresh the policy that actually owns it — frequently not the selected one. Falls
   * back to `refreshSelectedPlanDetails()` when the plan number is unknown.
   *
   * Preferred over `refreshAllPlanDetails()` for that flow on two counts: it is one request
   * instead of one per policy, and it does not assume `getAllPlanDetails` is the client's load
   * path — multi-policy is enabled on the client that loads via `getBulkPlanDetails`.
   *
   * On bulk-enabled clients this re-fetches through `getBulkPlanDetails` instead of the
   * plan-scoped `getPlanDetails` — the bulk endpoint is the ONLY source of `$bulkClaims` (see
   * the constructor's bulk subscription above), so a plan-scoped fetch would silently leave
   * `$recentClaims` — and therefore the dashboard's claims/service-requests carousel — showing
   * pre-refresh data on those clients. Still a single HTTP request either way.
   *
   * Drops the aggregate `:all` / `:bulk` session cache entries rather than rewriting them, so
   * the next page load rebuilds them from the API instead of resurrecting a pre-update snapshot
   * that disagrees with the per-plan entry this just refreshed.
   */
  refreshPlanDetailByNumber(planNumber: string | null | undefined, dealerCode?: string | null): Observable<PlanDetail | null> {
    if (!planNumber) {
      return this.refreshSelectedPlanDetails();
    }

    const plans = this.$plans.value;
    const plan = plans?.find((p) => p.planNumber === planNumber);
    if (!plan) {
      return this.refreshSelectedPlanDetails();
    }

    this.storageService.removeSession(`${AppConstants.STORAGE_KEYS.PlanDetail}:all`);
    this.storageService.removeSession(`${AppConstants.STORAGE_KEYS.PlanDetail}:bulk`);

    const source$ = this.isBulkPlanDetailsEnabled
      ? this.planService.getBulkPlanDetails(plans!, true).pipe(
          tap(({ claims }) => this.$bulkClaims.next(claims)),
          map(({ planDetails }) => planDetails.find((pd) => pd.planNumber === planNumber) ?? null)
        )
      : this.planService.getPlanDetails(planNumber, plan, true, dealerCode);

    return source$.pipe(
      tap((planDetail) => {
        if (!planDetail) {
          return;
        }
        const existing = this.$planDetails.value;
        if (existing) {
          const index = existing.findIndex((pd) => pd.planNumber === planNumber);
          this.$planDetails.next(index === -1 ? [...existing, planDetail] : existing.map((pd, i) => (i === index ? planDetail : pd)));
        }
        if (this.$selectedPlan.value?.planNumber === planNumber) {
          this.$selectedPlanDetail.next(planDetail);
        }
      }),
      catchError(() => of(null))
    );
  }

  refreshSelectedPlanDetails(): Observable<PlanDetail | null> {
    // Emit current values of selected plan and plans once, then complete
    return combineLatest({
      selectedPlan: this.$selectedPlan,
      plans: this.$plans
    }).pipe(
      take(1),
      switchMap(({ selectedPlan, plans }) => {
        if (!selectedPlan || !plans) {
          this.$selectedPlanDetail.next(null);
          return of(null);
        }

        return this.planService
          .getPlanDetails(selectedPlan.planNumber, selectedPlan, true, selectedPlan.dealerCode)
          .pipe(
            tap(planDetail => {
              this.$selectedPlanDetail.next(planDetail);
            }),
            catchError(error => {
              // console.error('Error fetching plan details:', error);
              this.$selectedPlanDetail.next(null);
              return of(null);
            })
          );
      })
    );
  }
}
